import { segmentGraphemes } from "@/lib/graphemes";

/**
 * Upside-down text: each character is swapped for the Unicode lookalike of its 180-degree
 * rotation, then the string is reversed (unless the order is kept).
 *
 * Capital B is ᗺ (U+15FA CANADIAN SYLLABICS CARRIER KHA). It looks like a B turned 180
 * degrees and sits in the same block as the ᗡ (U+15E1) already used for D, so it asks
 * for no extra font. The earlier Deseret capital bee (U+10412) is missing from the
 * default fonts of some phones and Windows setups.
 *
 * Capital K is ꓘ (U+A4D8 LISU LETTER KHA, Unicode 5.2), the only capital-height
 * lookalike for a turned K. The trade-off: Lisu is a smaller block than the lowercase
 * ʞ (U+029E LATIN SMALL LETTER TURNED K) that K used before, so a few older fonts lack
 * it, but ʞ is the wrong case. Lowercase k still maps to ʞ.
 */

// Each character paired with its closest 180-degree-rotated Unicode
// lookalike. Characters that are their own rotation (o, x, 0, 8, …) map to
// themselves; anything not listed passes through unchanged.
export const FLIP_PAIRS: [string, string][] = [
  ["a", "ɐ"],
  ["b", "q"],
  ["c", "ɔ"],
  ["d", "p"],
  ["e", "ǝ"],
  ["f", "ɟ"],
  ["g", "ƃ"],
  ["h", "ɥ"],
  ["i", "ᴉ"],
  ["j", "ɾ"],
  ["k", "ʞ"],
  ["l", "ן"],
  ["m", "ɯ"],
  ["n", "u"],
  ["o", "o"],
  ["p", "d"],
  ["q", "b"],
  ["r", "ɹ"],
  ["s", "s"],
  ["t", "ʇ"],
  ["u", "n"],
  ["v", "ʌ"],
  ["w", "ʍ"],
  ["x", "x"],
  ["y", "ʎ"],
  ["z", "z"],
  ["A", "∀"],
  ["B", "\u15FA"],
  ["C", "Ɔ"],
  ["D", "ᗡ"],
  ["E", "Ǝ"],
  ["F", "Ⅎ"],
  ["G", "⅁"],
  ["H", "H"],
  ["I", "I"],
  ["J", "ſ"],
  ["K", "\uA4D8"],
  ["L", "˥"],
  ["M", "W"],
  ["N", "N"],
  ["O", "O"],
  ["P", "Ԁ"],
  ["Q", "Ό"],
  ["R", "ᴚ"],
  ["S", "S"],
  ["T", "⊥"],
  ["U", "∩"],
  ["V", "Λ"],
  ["W", "M"],
  ["X", "X"],
  ["Y", "⅄"],
  ["Z", "Z"],
  ["0", "0"],
  ["1", "Ɩ"],
  ["2", "ᄅ"],
  ["3", "Ɛ"],
  ["4", "ㄣ"],
  ["5", "ϛ"],
  ["6", "9"],
  ["7", "ㄥ"],
  ["8", "8"],
  ["9", "6"],
  [".", "˙"],
  [",", "'"],
  ["?", "¿"],
  ["!", "¡"],
  ["'", ","],
  ['"', "„"],
  ["(", ")"],
  [")", "("],
  ["[", "]"],
  ["]", "["],
  ["{", "}"],
  ["}", "{"],
  ["<", ">"],
  [">", "<"],
  ["&", "⅋"],
  ["_", "‾"],
  [";", "؛"],
];

export const FLIP_MAP = new Map<string, string>(FLIP_PAIRS);

function flipChars(clusters: string[]): string[] {
  // Only a lone character has a rotated lookalike; an emoji, a flag or a letter with a
  // combining mark passes through whole.
  return clusters.map((cluster) => FLIP_MAP.get(cluster) ?? cluster);
}

export function flipText(text: string, mirror: boolean): string {
  // Work on user-perceived characters so reversing never splits a flag, a skin-tone
  // emoji, a ZWJ sequence or a letter from its combining mark.
  const flipped = flipChars(segmentGraphemes(text));
  if (mirror) {
    // Keep the original character order — reads backwards when rotated.
    return flipped.join("");
  }
  // Standard behavior: reverse so the text reads naturally when rotated 180°.
  return flipped.reverse().join("");
}
